import {applyInteractionId, mark, types as metrics, getInteractionId} from '@servicenow/ui-metrics';
import {errorTypes, locations, NOW_ID_PROP} from './constants';
import {allLogTypes as logTypes} from './logHandler';
import {findKey} from '@servicenow/ui-utils';
/**
 * Wrap the execution of code with a try catch and dispatch the error
 * @function sandbox
 * @private
 * @example
 * import {sandbox, errorTypes, locations} from '@servicenow/ui-internal'
 *
 * sandbox(
 *   onChange,
 *   {
 *     args: [value, previousValue, dispatch],
 *     dispatch,
 *     host,
 *     location: locations.LIFECYCLE,
 *     details: {name: 'onChange', propertyName},
 *     errorType: errorTypes.COMPONENT_ERROR_THROWN
 *   }
 * )
 *
 * @param {Function} fn - arbitrary function to execute
 * @param {Object} options - The sandbox options
 * @param {Array} [options.args] - Arguments to pass into the fn.
 * @param {Object} [options.dispatch] - Dispatch function, required.
 * @param {string} [options.location] - Enum of acceptable locations, import {locations} from '@servicenow/ui-internal', required.
 * @param {Object} [options.host] - The host most closely related to the executing code.
 * @param {Object} [options.details] - Object to store arbitrary data
 * @param {string} [options.errorType] - Type of action to dispatch on error caught. Default is COMPONENT_ERROR_THROWN
 * @param {Object} [options.metadata] - Metadata to be passed to the dispatch function
 * @param {function} [options.log] - Logger to log the error
 * @returns {any} Value returned from the function
 */
function sandbox(fn, options = {}) {
	const {args = []} = options;
	try {
		const value = fn(...args);
		return value;
	} catch (error) {
		notify(error, options);
	}
}

export function notify(
	error,
	{
		dispatch,
		host,
		location,
		details = {},
		errorType = errorTypes.COMPONENT_ERROR_THROWN,
		metadata = {},
		log
	}
) {
	const tagName = host.tagName && host.tagName.toLowerCase();
	const componentId = host[NOW_ID_PROP];
	const errorActionType = findKey(errorTypes, val => val === errorType);

	applyInteractionId(metadata);
	mark(host, getInteractionId(metadata), metrics.ERROR, {location});

	// This if statement prevents a recursive loop when an error is thrown within the COMPONENT_ERROR_THROWN effect
	if (!isRecursive({location, details}))
		dispatch(errorType, {host, location, details, error}, {internal: true, ...metadata}, true);

	if (log) {
		log(
			`An Error occured in <${tagName} component-id="${componentId}"/>. ${errorActionType} action type is dispatched with error details.`,
			{error, level: logTypes.ERROR, host, origin: 'sandbox'}
		);
	}
}

function isRecursive({location, details}) {
	if (location === locations.EFFECT && details.actionType === errorTypes.COMPONENT_ERROR_THROWN)
		return true;

	return false;
}

export default sandbox;
